/**
 * format.ts — 数据格式化工具（取代原型的 window.fmtCountry / window.fmtLang）
 *
 * 设计系统「数据格式」约定：国家/语言用「大写代码 + 空格 + 中文」；数字 mono、金额欧元、百分比 1 位小数。
 * 见 design-system「数据格式」模式页。
 */

// ---------- 国家名（系统标准：CODE + 中文名）----------
export const COUNTRY_NAMES: Record<string, string> = {
  DE: '德国',
  FR: '法国',
  IT: '意大利',
  ES: '西班牙',
  PT: '葡萄牙',
  UK: '英国',
  GB: '英国',
  IE: '爱尔兰',
  NL: '荷兰',
  BE: '比利时',
  DK: '丹麦',
  SE: '瑞典',
  FI: '芬兰',
  NO: '挪威',
  IS: '冰岛',
  PL: '波兰',
  CZ: '捷克',
  SK: '斯洛伐克',
  HU: '匈牙利',
  AT: '奥地利',
  CH: '瑞士',
  RO: '罗马尼亚',
};

const COUNTRY_DISPLAY_NAMES = new Intl.DisplayNames(['zh-CN'], { type: 'region' });

/** 国家代码 → "DE 德国"；优先保留业务常用译名，其余 ISO 地区码走浏览器内置中文名 */
export function fmtCountry(code?: string | null): string {
  if (!code) return '';
  const c = String(code).toUpperCase();
  // ZZ 在分析数据里代表未知地区，不能被 Intl 翻译成「未知地区」后误当作真实国家名。
  const name = COUNTRY_NAMES[c] ?? (c === 'ZZ' ? undefined : COUNTRY_DISPLAY_NAMES.of(c));
  return name ? `${c} ${name}` : c;
}

// ---------- 语言名（系统标准：CODE + 中文名）----------
/**
 * 语言中文名 SSOT = `system_languages.display_name_i18n.zh`（C 端表，admin 只读）。
 * 运行时由 useSysConfig 拉到后经 registerLangNames 注入下表；本内置表只做
 * **sys-config 未加载 / 该语言缺 zh 名**时的兜底，用词与 DB seed 保持一致（「X语」，zh 为「中文」）。
 * 新增语言由 DB seed 驱动、前端零改动——除非兜底期也要有名字，否则不必回来加键。
 */
export const LANG_NAMES: Record<string, string> = {
  zh: '中文',
  en: '英语',
  de: '德语',
  fr: '法语',
  it: '意大利语',
  es: '西班牙语',
  pt: '葡萄牙语',
  // BCP-47 区域码（system_languages 里 pt 与 pt-BR 是两个独立成员）；键统一小写，查表前 toLowerCase
  'pt-br': '巴西葡萄牙语',
  nl: '荷兰语',
  pl: '波兰语',
  cs: '捷克语',
  sk: '斯洛伐克语',
  hu: '匈牙利语',
  sv: '瑞典语',
  da: '丹麦语',
  nb: '挪威语',
  fi: '芬兰语',
  is: '冰岛语',
  ro: '罗马尼亚语',
  ru: '俄语',
};

/**
 * 运行时语言名表（DB 真值），由 registerLangNames 注入；键小写。
 * 模块级可变状态是刻意的：langName / fmtLang 是纯函数、被非 React 上下文（adapter / 格式化）调用，
 * 无法读 hook；注册幂等，未注册时整表为空、自动回退 LANG_NAMES。
 */
let runtimeLangNames: Record<string, string> = {};

/** 注入 DB 语言名（system_languages.display_name_i18n.zh）；键会被规范成小写 */
export function registerLangNames(names: Record<string, string | undefined>): void {
  const next: Record<string, string> = {};
  for (const [code, name] of Object.entries(names)) {
    if (name) next[code.toLowerCase()] = name;
  }
  runtimeLangNames = next;
}

/** 语言代码 → 纯中文名（无 CODE 前缀）；DB 真值优先，未知码返回空串 */
export function langName(code?: string | null): string {
  if (!code) return '';
  const c = String(code).toLowerCase();
  return runtimeLangNames[c] || LANG_NAMES[c] || '';
}

/** 语言代码 → "EN 英语"（全系统统一「代码 + 中文名」）；无中文名时只出代码 */
export function fmtLang(code?: string | null): string {
  if (!code) return '';
  const c = String(code).toLowerCase();
  const name = langName(c);
  return name ? `${c.toUpperCase()} ${name}` : c.toUpperCase();
}

// ---------- 数字 / 货币 / 百分比 ----------
/** 千分位整数 */
export function fmtNum(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

/** 紧凑大数 12,456 · 1.14M · 23B */
export function fmtCompact(n: number): string {
  if (Math.abs(n) >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  return fmtNum(n);
}

/** 币种码 → 符号（EUR→€ / USD→$ / GBP→£ / PLN→zł；其它回退「CODE 」前缀）*/
const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: '€',
  USD: '$',
  GBP: '£',
  PLN: 'zł',
};
export function currencySymbol(currency = 'EUR'): string {
  return CURRENCY_SYMBOLS[currency] ?? `${currency} `;
}

/** 金额 $128.40 / €128.40（千分位 + 2 位小数）*/
export function fmtMoney(n: number, currency = 'EUR'): string {
  return `${currencySymbol(currency)}${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** 百分比 12.4%（1 位小数）*/
export function fmtPct(n: number, digits = 1): string {
  return `${n.toFixed(digits)}%`;
}

/**
 * 币种 minor-unit exponent（ISO 4217）：EUR/PLN/GBP=2、JPY/HUF=0、…。用 Intl 解析（Node/浏览器内置全 ICU），
 * 未知 / 非法币种码回退 2。决定 minor→major 的除数（10^exp）+ 展示小数位（codex#80：禁硬编码 /100）。
 */
function currencyExponent(currency: string): number {
  try {
    return (
      new Intl.NumberFormat('en-US', { style: 'currency', currency }).resolvedOptions()
        .maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

/**
 * minor units（bigint string）→ 主单位展示串，按 exponent 拆整数/小数部分（codex#80 P2.2：禁硬编码 /100）。
 * **全程 BigInt 不经 Number**（codex#80 重审 P2）：amount_minor_units 故意以 string 承载大额（server 端聚合用
 * bigint、测试覆盖 > 2^53），若 `Number(str)` 转换会在超 2^53 时丢精度致金额错显。整数部分用 `BigInt.toLocaleString`
 * 加千分位（BigInt 原生支持、不丢精度），小数部分按 exponent 零填充。非法/空串回退原样返回。
 */
function fmtMinorUnits(minorStr: string, exp: number): string {
  let v: bigint;
  try {
    v = BigInt(minorStr);
  } catch {
    return minorStr;
  }
  const neg = v < 0n;
  if (neg) v = -v;
  const sign = neg ? '-' : '';
  const divisor = 10n ** BigInt(exp);
  const whole = (v / divisor).toLocaleString('en-US'); // 千分位，BigInt 原生不丢精度
  if (exp === 0) return `${sign}${whole}`;
  const frac = (v % divisor).toString().padStart(exp, '0');
  return `${sign}${whole}.${frac}`;
}

/** 图表与既有数值聚合使用主单位 number；精确金额展示仍走 fmtCurrencyMinor。 */
export function currencyMinorToMajorNumber(amountMinorUnits: string, currency: string): number {
  const amount = Number(amountMinorUnits);
  return Number.isFinite(amount) ? amount / 10 ** currencyExponent(currency) : 0;
}

/** 原币minor单位按ISO币种小数位转展示主单位，复用BigInt格式化保持大额精度。 */
/** IAA 可显式传入契约精度，避免浏览器 Intl 货币表版本影响精确展示。 */
export function fmtCurrencyMinor(
  amountMinorUnits: string,
  currency: string,
  exponent = currencyExponent(currency),
): string {
  return fmtMinorUnits(amountMinorUnits, exponent);
}

/**
 * 累计充值 per-currency 数组 → 展示串「€128.40 · zł45.00」（catch-up #130，ADM-USR total_spend/net_spend）。
 * - null（数据源不可得）/ [] （无充值）→ '—'（两者展示一致；调用方无需区分）。
 * - 各币种 minor units `amount_minor_units` 按**该币种 ISO 4217 exponent** 转主单位（codex#80：EUR/PLN /100 + 2 位，
 *   JPY/HUF /1 + 0 位——禁硬编码 /100，否则 JPY 500→"¥5.00" 错显）；多币种 MVP 不做汇率归一，逐币种 ` · ` 拼接。
 * - 金额全程走 BigInt（见 fmtMinorUnits），超 2^53 不丢精度（codex#80 重审 P2）。
 */
export function fmtSpendByCurrency(
  spend: { currency: string; amount_minor_units: string }[] | null | undefined,
): string {
  if (spend == null || spend.length === 0) return '—';
  return spend
    .map(
      (s) =>
        `${currencySymbol(s.currency)}${fmtMinorUnits(s.amount_minor_units, currencyExponent(s.currency))}`,
    )
    .join(' · ');
}

// ---------- 日期 / 时间（系统标准：YYYY-MM-DD HH:mm，本地时区）----------
/**
 * 把 ISO 8601 时间戳拆成本地时区的零填充零件；空串 / 非法时间返回 null（调用方据此回退）。
 * 设计约定：按浏览器本地时区展示（运营直觉所需），与原型「2026-05-22 14:08」格式一致；
 * 存储恒为 UTC（ISO with Z），仅展示层本地化。绝不向用户显示 "Invalid Date" 或裸 ISO。
 */
function localDateParts(
  iso?: string | null,
): { y: string; mo: string; d: string; h: string; mi: string; s: string } | null {
  if (!iso) return null;
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    y: String(dt.getFullYear()),
    mo: pad(dt.getMonth() + 1),
    d: pad(dt.getDate()),
    h: pad(dt.getHours()),
    mi: pad(dt.getMinutes()),
    s: pad(dt.getSeconds()),
  };
}

/** ISO 时间戳 → "2026-05-22"（本地时区，仅日期）；空 / 非法返回空串。替代各处手动 `.slice(0, 10)` */
export function fmtDate(iso?: string | null): string {
  const t = localDateParts(iso);
  return t ? `${t.y}-${t.mo}-${t.d}` : '';
}

/** 秒数 → "45 秒" / "3 分 05 秒" / "2 小时 07 分"；负数与非法值按 0 处理。用于执行耗时与「多久前」展示 */
export function fmtDurationSeconds(totalSeconds: number): string {
  const seconds = Number.isFinite(totalSeconds) ? Math.max(0, Math.floor(totalSeconds)) : 0;
  if (seconds < 60) return `${seconds} 秒`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} 分 ${String(seconds % 60).padStart(2, '0')} 秒`;
  return `${Math.floor(minutes / 60)} 小时 ${String(minutes % 60).padStart(2, '0')} 分`;
}

/** ISO 时间戳 → "2026-05-22 14:08"（本地时区，YYYY-MM-DD HH:mm，对齐设计稿）；空 / 非法返回空串 */
export function fmtDateTime(iso?: string | null): string {
  const t = localDateParts(iso);
  return t ? `${t.y}-${t.mo}-${t.d} ${t.h}:${t.mi}` : '';
}

/**
 * 发布计划绝对时刻 → 指定 IANA 时区墙上时刻，并显式附时区名。
 * 时区缺失/非法时回退 UTC（仍显式标注），绝不借用浏览器本地时区造成运营误读。
 */
export function fmtDateTimeInTimeZone(iso?: string | null, timeZone?: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const selectedTimeZone = timeZone || 'UTC';
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: selectedTimeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date);
    const value = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((part) => part.type === type)?.value ?? '';
    return `${value('year')}-${value('month')}-${value('day')} ${value('hour')}:${value('minute')} (${selectedTimeZone})`;
  } catch {
    return `${date.toISOString().slice(0, 16).replace('T', ' ')} (UTC)`;
  }
}

/**
 * ISO 时间戳 → "2026-05-22 14:08:33"（本地时区，YYYY-MM-DD HH:mm:ss，到秒）；空 / 非法返回空串。
 * 审计/操作日志专用：秒级精度对账（对齐操作日志设计稿），不替代各处分钟精度的 fmtDateTime。
 */
export function fmtDateTimeSec(iso?: string | null): string {
  const t = localDateParts(iso);
  return t ? `${t.y}-${t.mo}-${t.d} ${t.h}:${t.mi}:${t.s}` : '';
}

/**
 * IP 脱敏展示：IPv4 末段 → x（68.64.149.52 → 68.64.149.x）；IPv6 仅留前 3 段 + ::x；
 * 非 IP / 空 → 原样（空串返 ''）。审计列表防截图/旁观泄露完整来源 IP（完整 IP 仍在展开区 + CSV 供取证）。
 */
export function maskIp(ip?: string | null): string {
  if (!ip) return '';
  if (ip.includes('.')) {
    const parts = ip.split('.');
    if (parts.length === 4) return `${parts[0]}.${parts[1]}.${parts[2]}.x`;
    return ip;
  }
  if (ip.includes(':')) {
    const groups = ip.split(':').filter(Boolean);
    return groups.length > 3 ? `${groups.slice(0, 3).join(':')}::x` : ip;
  }
  return ip;
}
